import { useState, useRef, useEffect, useCallback } from 'react'
import { FilePlus, Search, Sun, Moon, Cloud, CloudOff, LogOut, Settings, Loader2, Pencil, Menu, X } from 'lucide-react'
import { Tooltip } from './ui/Tooltip'
import { BuyMeACoffeeButton } from './BuyMeACoffeeButton'
import type { Theme } from '../hooks/useTheme'


interface ToolbarProps {
  noteTitle: string
  customTitle: string | null
  onTitleChange: (title: string | null) => void
  onNewNote: () => void
  onOpenPalette: () => void
  onSync: () => void
  isAuthenticated: boolean
  authLoading: boolean
  theme: Theme
  onToggleTheme: () => void
  userEmail: string | null
  onSignOut: () => void | Promise<void>
  onSettings: () => void
}

export function Toolbar({
  noteTitle,
  customTitle,
  onTitleChange,
  onNewNote,
  onOpenPalette,
  onSync,
  isAuthenticated,
  authLoading,
  theme,
  onToggleTheme,
  userEmail,
  onSignOut,
  onSettings,
}: ToolbarProps) {
  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0
  const modKey = isMac ? '⌘' : 'Ctrl+'

  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [isSigningOut, setIsSigningOut] = useState(false)
  const [isTitleFocused, setIsTitleFocused] = useState(false)
  const titleInputRef = useRef<HTMLInputElement>(null)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const mobileMenuRef = useRef<HTMLDivElement>(null)

  // Close user menu on outside click
  useEffect(() => {
    if (!showUserMenu) return

    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showUserMenu])

  // Close mobile menu on outside click
  useEffect(() => {
    if (!showMobileMenu) return

    const handleClickOutside = (e: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setShowMobileMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showMobileMenu])

  // Commit title on blur
  const handleTitleBlur = useCallback(() => {
    setIsTitleFocused(false)
    const value = titleInputRef.current?.value.trim() ?? ''
    onTitleChange(value || null)
  }, [onTitleChange])

  // Handle key events in title input
  const handleTitleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      titleInputRef.current?.blur()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      if (titleInputRef.current) {
        titleInputRef.current.value = customTitle ?? ''
      }
      titleInputRef.current?.blur()
    }
  }, [customTitle])

  // User initial for avatar
  const userInitial = userEmail ? userEmail.charAt(0).toUpperCase() : '?'

  // Sync button renderer — shared between desktop and mobile menu
  const renderSyncButton = (mobile = false) => {
    if (authLoading) {
      return (
        <div
          className={mobile ? 'flex items-center gap-3 px-4 py-3 opacity-50' : 'p-2 rounded-lg'}
          style={{ color: 'hsl(var(--color-text-muted))' }}
        >
          <Cloud className="w-4 h-4 opacity-50" />
          {mobile && <span className="text-sm">Loading...</span>}
        </div>
      )
    }
    if (!isAuthenticated) {
      return mobile ? (
        <button
          onClick={() => { setShowMobileMenu(false); onSync() }}
          className="w-full flex items-center gap-3 px-4 py-3 text-sm transition-fast hover:opacity-80"
          style={{ color: 'hsl(var(--color-text-primary))' }}
        >
          <CloudOff className="w-4 h-4" />
          Enable Sync
        </button>
      ) : (
        <Tooltip content="Sign in to enable cloud sync">
          <button
            onClick={onSync}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-fast hover:bg-black/10 dark:hover:bg-white/10 border"
            style={{
              borderColor: 'hsl(var(--color-border))',
              color: 'hsl(var(--color-text-primary))'
            }}
          >
            <CloudOff className="w-3.5 h-3.5" />
            <span
              className="text-[11px] font-medium"
              style={{ color: 'hsl(var(--color-text-muted))' }}
            >
              Enable Sync
            </span>
          </button>
        </Tooltip>
      )
    }
    return null
  }

  return (
    <header
      className="h-12 sm:h-14 flex items-center justify-between px-2 sm:px-5 border-b gap-1 sm:gap-4"
      style={{
        backgroundColor: 'hsl(var(--color-bg-secondary))',
        borderColor: 'hsl(var(--color-border))'
      }}
    >
      {/* Left: Branding */}
      <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
        <a
          href="/home"
          className="text-lg sm:text-xl font-bold tracking-tight hover:opacity-80 transition-opacity"
          style={{ fontFamily: "'Space Grotesk', sans-serif", color: 'hsl(var(--color-text-primary))' }}
        >
          JOT
        </a>
      </div>

      {/* Center: Note title — fills remaining space between branding and actions */}
      <div className="flex-1 flex items-center justify-center min-w-0 mx-1 sm:mx-4">
        <div className="max-w-[300px] w-full min-w-0">
          {isTitleFocused ? (
            <div className="relative flex items-center w-full">
              <input
                ref={titleInputRef}
                type="text"
                defaultValue={customTitle ?? ''}
                key={customTitle ?? ''}
                onBlur={handleTitleBlur}
                onKeyDown={handleTitleKeyDown}
                placeholder={noteTitle || 'Untitled'}
                autoFocus
                className="text-xs sm:text-sm font-medium text-center bg-transparent outline-none w-full py-1 sm:py-1.5 px-2 sm:px-3 pr-7 sm:pr-8 rounded-lg border transition-all duration-200"
                style={{
                  color: 'hsl(var(--color-text-primary))',
                  borderColor: 'hsl(var(--color-accent))',
                  backgroundColor: 'hsl(var(--color-bg-primary))',
                  boxShadow: '0 0 0 2px hsl(var(--color-accent) / 0.15)',
                }}
              />
              <Pencil
                className="absolute right-2 sm:right-2.5 w-3 h-3 sm:w-3.5 sm:h-3.5 pointer-events-none"
                style={{ color: 'hsl(var(--color-accent))' }}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setIsTitleFocused(true)}
              className="group relative flex items-center justify-center gap-1 w-full py-1 sm:py-1.5 px-2 sm:px-3 rounded-lg transition-all duration-200 cursor-pointer hover:bg-[hsl(var(--color-bg-tertiary)/0.5)]"
            >
              <span
                className="text-xs sm:text-sm font-medium truncate transition-colors duration-200 group-hover:!text-[hsl(var(--color-accent))]"
                style={{
                  color: customTitle
                    ? 'hsl(var(--color-text-primary))'
                    : 'hsl(var(--color-text-secondary))',
                }}
              >
                {customTitle || noteTitle || 'Untitled'}
              </span>
              <Pencil
                className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0 opacity-0 -translate-x-1 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0"
                style={{ color: 'hsl(var(--color-accent))' }}
              />
            </button>
          )}
        </div>
      </div>

      {/* Right: Coffee + mobile hamburger + desktop actions */}
      <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
        <BuyMeACoffeeButton source="editor_toolbar" variant="toolbar" />

        {/* Mobile hamburger menu trigger (visible < 640px) */}
        <div className="sm:hidden relative" ref={mobileMenuRef}>
          <button
            onClick={() => setShowMobileMenu(prev => !prev)}
            className="p-1.5 rounded-lg transition-fast hover:bg-black/10 dark:hover:bg-white/10"
            style={{ color: 'hsl(var(--color-text-primary))' }}
          >
            {showMobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          {showMobileMenu && (
            <div
              className="absolute right-0 top-full mt-2 w-56 rounded-xl shadow-lg border overflow-hidden z-50"
              style={{
                backgroundColor: 'hsl(var(--color-bg-secondary))',
                borderColor: 'hsl(var(--color-border))'
              }}
            >
              <button
                onClick={() => { setShowMobileMenu(false); onNewNote() }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm transition-fast hover:opacity-80"
                style={{ color: 'hsl(var(--color-text-primary))' }}
              >
                <FilePlus className="w-4 h-4" />
                New Note
              </button>

              <button
                onClick={() => { setShowMobileMenu(false); onOpenPalette() }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm transition-fast hover:opacity-80"
                style={{ color: 'hsl(var(--color-text-primary))' }}
              >
                <Search className="w-4 h-4" />
                Search Notes
              </button>

              {renderSyncButton(true)}

              <div className="border-t" style={{ borderColor: 'hsl(var(--color-border))' }} />

              <button
                onClick={() => { setShowMobileMenu(false); onToggleTheme() }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm transition-fast hover:opacity-80"
                style={{ color: 'hsl(var(--color-text-primary))' }}
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </button>

              {isAuthenticated && !authLoading && (
                <>
                  <div className="border-t" style={{ borderColor: 'hsl(var(--color-border))' }} />
                  <div className="px-4 py-2.5 truncate">
                    <p
                      className="text-xs font-medium truncate"
                      style={{ color: 'hsl(var(--color-text-secondary))' }}
                    >
                      {userEmail}
                    </p>
                  </div>
                  <button
                    onClick={() => { setShowMobileMenu(false); onSettings() }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm transition-fast hover:opacity-80"
                    style={{ color: 'hsl(var(--color-text-primary))' }}
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </button>
                  <button
                    onClick={async () => {
                      setIsSigningOut(true)
                      setShowMobileMenu(false)
                      try { await onSignOut() } catch { setIsSigningOut(false) }
                    }}
                    disabled={isSigningOut}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm transition-fast hover:opacity-80 disabled:opacity-60"
                    style={{ color: 'hsl(var(--color-text-primary))' }}
                  >
                    {isSigningOut ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
                    {isSigningOut ? 'Signing out...' : 'Sign Out'}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Desktop actions (visible >= 640px) */}
        <Tooltip content="New Note">
          <button
            onClick={onNewNote}
            className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-fast hover:bg-black/10 dark:hover:bg-white/10 border"
            style={{
              borderColor: 'hsl(var(--color-border))',
              color: 'hsl(var(--color-text-primary))'
            }}
          >
            <FilePlus className="w-3.5 h-3.5" />
            <span
              className="text-[11px] font-medium"
              style={{ color: 'hsl(var(--color-text-muted))' }}
            >
              New
            </span>
          </button>
        </Tooltip>

        <div className="hidden sm:block">
          {renderSyncButton(false)}
        </div>

        <button
          onClick={onOpenPalette}
          className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-fast hover:bg-black/10 dark:hover:bg-white/10 border mr-1"
          style={{
            borderColor: 'hsl(var(--color-border))',
            color: 'hsl(var(--color-text-primary))'
          }}
          title="Search commands and notes"
        >
          <Search
            className="w-3.5 h-3.5"
            style={{ color: 'hsl(var(--color-text-muted))' }}
          />
          <span
            className="text-[11px] font-medium"
            style={{ color: 'hsl(var(--color-text-muted))' }}
          >
            Search
          </span>
          <kbd
            className="inline-flex items-center px-1 py-px text-[10px] rounded border font-sans"
            style={{
              backgroundColor: 'hsl(var(--color-bg-tertiary))',
              borderColor: 'hsl(var(--color-border))',
              color: 'hsl(var(--color-text-muted))'
            }}
          >
            {modKey}K
          </kbd>
        </button>

        <Tooltip content={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={onToggleTheme}
            className="hidden sm:flex p-2 rounded-lg transition-fast hover:bg-black/10 dark:hover:bg-white/10"
            style={{ color: 'hsl(var(--color-accent))' }}
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
        </Tooltip>

        {isAuthenticated && !authLoading && (
          <div className="relative hidden sm:block" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(prev => !prev)}
              className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-fast hover:opacity-90"
              style={{
                backgroundColor: 'hsl(var(--color-accent))',
                color: 'hsl(var(--color-bg-primary))'
              }}
              title={userEmail ?? 'Account'}
            >
              {userInitial}
            </button>

            {showUserMenu && (
              <div
                className="absolute right-0 top-full mt-2 w-56 rounded-xl shadow-lg border overflow-hidden z-50"
                style={{
                  backgroundColor: 'hsl(var(--color-bg-secondary))',
                  borderColor: 'hsl(var(--color-border))'
                }}
              >
                <div
                  className="px-4 py-3 border-b truncate"
                  style={{ borderColor: 'hsl(var(--color-border))' }}
                >
                  <p
                    className="text-xs font-medium truncate"
                    style={{ color: 'hsl(var(--color-text-secondary))' }}
                  >
                    Signed in as
                  </p>
                  <p
                    className="text-sm font-medium truncate mt-0.5"
                    style={{ color: 'hsl(var(--color-text-primary))' }}
                  >
                    {userEmail}
                  </p>
                </div>

                <button
                  onClick={() => {
                    setShowUserMenu(false)
                    onSettings()
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-fast hover:opacity-80"
                  style={{ color: 'hsl(var(--color-text-primary))' }}
                >
                  <Settings className="w-4 h-4" />
                  Settings
                </button>

                <button
                  onClick={async () => {
                    setIsSigningOut(true)
                    setShowUserMenu(false)
                    try {
                      await onSignOut()
                    } catch {
                      setIsSigningOut(false)
                    }
                  }}
                  disabled={isSigningOut}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-fast hover:opacity-80 disabled:opacity-60"
                  style={{ color: 'hsl(var(--color-text-primary))' }}
                >
                  {isSigningOut ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <LogOut className="w-4 h-4" />
                  )}
                  {isSigningOut ? 'Signing out...' : 'Sign out'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  )
}
